import { useEffect, useRef } from 'react';

const HERBAL_BENEFITS = [
  { icon: 'ri-heart-pulse-line', text: '불안 · 초조 완화', sub: 'Anxiety Relief' },
  { icon: 'ri-moon-line', text: '불면 개선', sub: 'Better Sleep' },
  { icon: 'ri-brain-line', text: '두통 · 기억력 증진', sub: 'Cognitive Boost' },
  { icon: 'ri-mental-health-line', text: '스트레스 해소', sub: 'Stress Relief' },
];

const DIET_BENEFITS = [
  { icon: 'ri-fire-line', text: '지방 분해 촉진', sub: 'Fat Metabolism' },
  { icon: 'ri-leaf-line', text: '쾌변 · 숙변 제거', sub: 'Detox & Cleanse' },
  { icon: 'ri-scales-line', text: '체질 개선', sub: 'Body Constitution' },
  { icon: 'ri-seedling-line', text: '천연 한약재', sub: 'Natural Herbal' },
];

export default function DietHerbalSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="diet-herbal" ref={ref} className="bg-white py-20 px-7 md:py-28 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20 section-reveal">
          <span className="inline-block text-gold font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Diet &amp; Herbal Care · 减肥韩方 · ダイエット漢方
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-4">다이어트 & 한방 케어</h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-5"></div>
          <p className="font-sans text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
            한의학 박사가 체질을 분석하고, 개인 맞춤 한약으로 내부부터 건강하게 바꿉니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 md:gap-14">
          {/* 안신환+청뇌환 Card */}
          <div className="section-reveal bg-cream border border-gold/15 rounded-2xl overflow-hidden hover:border-gold/40 transition-colors duration-300">
            <div className="w-full bg-cream flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
              <img
                src="https://static.readdy.ai/image/acf1e7584863ae051c8c9ee92765da8b/5a53422c3bfb9d611a84fa6bbaeb3dbb.jpeg"
                alt="안신환+청뇌환"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="p-7 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-plum text-white text-xs px-3 py-1 rounded-full font-sans font-semibold whitespace-nowrap">한방 케어</span>
                <span className="border border-plum text-plum text-xs px-3 py-1 rounded-full font-sans whitespace-nowrap">한의학 박사 처방</span>
              </div>
              <h3 className="font-serif text-2xl font-bold text-gray-900 mb-1">안신환 + 청뇌환</h3>
              <p className="font-sans text-sm text-plum mb-3">Ansinhwan · 安神丸 · 安神丸（あんしんがん）</p>
              <p className="font-sans text-sm text-gray-600 leading-relaxed mb-5">
                불안, 초조, 불면, 두통, 기억력 감퇴 등 현대인의 정신적 스트레스를 한의학적으로 근본 개선합니다. 한의학 박사가 직접 체질을 진단하고 처방합니다.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {HERBAL_BENEFITS.map((b) => (
                  <div key={b.text} className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center text-gold">
                      <i className={`${b.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="font-sans text-xs font-medium text-gray-800">{b.text}</p>
                      <p className="font-sans text-xs text-gray-400">{b.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between bg-white rounded-xl p-4 border border-gold/15">
                <div>
                  <p className="font-sans text-xs text-gray-400 mb-0.5">이벤트 특가</p>
                  <p className="font-serif text-xl font-bold text-plum">350,000원</p>
                </div>
                <button
                  onClick={() => { const el = document.querySelector('#reservation'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                  className="bg-plum text-white px-4 py-2 rounded-full text-sm font-sans font-semibold whitespace-nowrap cursor-pointer hover:bg-plum-dark transition-colors"
                >
                  예약하기
                </button>
              </div>
            </div>
          </div>

          {/* 슬림 다이어트 Card */}
          <div className="section-reveal bg-cream border border-gold/15 rounded-2xl overflow-hidden hover:border-gold/40 transition-colors duration-300">
            <div className="w-full bg-cream flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
              <img
                src="https://static.readdy.ai/image/acf1e7584863ae051c8c9ee92765da8b/0bca3d0d64534163c163221ec5f65130.jpeg"
                alt="슬림 다이어트 한약"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="p-7 sm:p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-gold text-white text-xs px-3 py-1 rounded-full font-sans font-semibold whitespace-nowrap">다이어트 한약</span>
                <span className="border border-gold text-gold-dark text-xs px-3 py-1 rounded-full font-sans whitespace-nowrap">Slim Diet</span>
              </div>
              <h3 className="font-serif text-2xl font-bold text-gray-900 mb-1">슬림 다이어트 한약</h3>
              <p className="font-sans text-sm text-gold-dark mb-3">Slim Diet Herb · 减肥韩药 · スリムダイエット漢薬</p>
              <p className="font-sans text-sm text-gray-600 leading-relaxed mb-5">
                체질에 맞는 맞춤 한약으로 지방을 분해하고, 쾌변과 숙변 제거를 통해 속부터 가볍게 만들어드립니다. 부작용 없는 자연스러운 체중 관리가 가능합니다.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {DIET_BENEFITS.map((b) => (
                  <div key={b.text} className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center text-gold">
                      <i className={`${b.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="font-sans text-xs font-medium text-gray-800">{b.text}</p>
                      <p className="font-sans text-xs text-gray-400">{b.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between bg-white rounded-xl p-4 border border-gold/15">
                <div>
                  <p className="font-sans text-xs text-gray-400 mb-0.5">이벤트 특가</p>
                  <p className="font-serif text-xl font-bold text-gold-dark">300,000원</p>
                </div>
                <button
                  onClick={() => { const el = document.querySelector('#reservation'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                  className="bg-gold text-white px-4 py-2 rounded-full text-sm font-sans font-semibold whitespace-nowrap cursor-pointer hover:bg-gold-dark transition-colors"
                >
                  예약하기
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Doctor Info Strip */}
        <div className="mt-10 md:mt-14 section-reveal bg-gradient-to-r from-plum-pale via-white to-gold-pale border border-gold/20 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-center gap-5 sm:gap-6 text-center sm:text-left">
          <div className="w-14 h-14 flex items-center justify-center bg-plum rounded-full text-white flex-shrink-0">
            <i className="ri-stethoscope-line text-2xl"></i>
          </div>
          <div className="text-center sm:text-left">
            <p className="font-serif text-lg font-bold text-gray-900 mb-1">한의학 박사 상주 클리닉</p>
            <p className="font-sans text-sm text-gray-600">
              모든 한방 케어 상담은 <strong>한의학 박사</strong>가 직접 체질을 진단하고 개인 맞춤 처방을 제공합니다.
              <span className="text-plum font-medium"> · A Ph.D. in Oriental Medicine is always on-site for personalized consultations.</span>
            </p>
          </div>
          <a
            href="tel:01067404061"
            className="w-full sm:w-auto flex-shrink-0 bg-plum text-white px-6 py-3 rounded-full font-sans font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-plum-dark transition-colors text-center"
          >
            <i className="ri-phone-line mr-1.5"></i>무료 상담
          </a>
        </div>
      </div>
    </section>
  );
}
